/** Sites list — the tenant's sites, with the entry point to the onboarding wizard. */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../auth';
import { ApiError, type Site } from '../api';
import { Banner } from '../ui';
import { FleetMap } from '../FleetMap';

export function Sites(): JSX.Element {
  const { api, session } = useAuth();
  const [sites, setSites] = useState<Site[] | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    api
      .listSites()
      .then((r) => live && setSites(r.sites))
      .catch((e) => live && setErr(e instanceof ApiError ? e.message : 'Could not load sites.'));
    return () => {
      live = false;
    };
  }, [api]);

  const canCreate = session?.role === 'tenant_admin';

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Sites</h1>
          <div className="lead">Every monitored site in this workspace.</div>
        </div>
        {canCreate && (
          <Link className="btn btn-primary" to="/sites/new">
            + New site
          </Link>
        )}
      </div>

      {err && <Banner kind="err">{err}</Banner>}

      {sites === null && !err ? (
        <div className="empty">Loading…</div>
      ) : sites && sites.length === 0 ? (
        <div className="panel">
          <div className="empty">
            <h3>No sites yet</h3>
            <p>
              Onboard your first site — create it, register its RUT956 gateway, attach the DSE
              controller, and issue the gateway certificate.
            </p>
            {canCreate ? (
              <Link className="btn btn-primary" to="/sites/new">
                Onboard a site
              </Link>
            ) : (
              <p className="faint">Ask a workspace admin to add one.</p>
            )}
          </div>
        </div>
      ) : (
        <>
          <FleetMap sites={sites ?? []} />
          <div className="stack">
            {sites?.map((s) => (
              <Link className="row-card" to={`/sites/${s.id}`} key={s.id}>
                <div>
                  <div className="title">{s.name}</div>
                  <div className="meta">
                    <span className="mono">{s.site_code}</span>
                    {s.region ? ` · ${s.region}` : ''}
                  </div>
                </div>
                <div className="row-right">
                  {s.criticality && (
                    <span className={`pill ${s.criticality === 'high' ? 'crit' : 'idle'}`}>
                      {s.criticality}
                    </span>
                  )}
                  <span className="faint" aria-hidden="true">
                    →
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </>
      )}
    </>
  );
}
